/**************************************************/
/* Methods for DA based on wasm code              */
/**************************************************/

import { alloc, allocCopy, checkModelBuffer, wasmError } from './utils.js';

/**
 * Adjusts PLS ncomp to DA ncomp
 * @param {number} nresp number of responses (classes).
 * @param {number} ncomp number of components in PLS model.
 *
 * @returns {number} index of corresponding DA model starting from 0.
 */
export function dancomp(nresp, ncomp) {

   if (ncomp < nresp - 1) {
      throw new Error('dancomp: wrong combination of "nresp" and "ncomp" values.')
   }

   return ncomp - nresp + 1;
}


/**
 * Returns length of array with DA results.
 *
 * @param {number} nrows number of rows in a dataset.
 * @param {number} nresp number of response variables (number of classes).
 * @param {number} ncomp number of DA blocks (PLS components from (nresp - 1) to pls_ncomp).
 *
 * @description if number of classes (and hence responses is 3), and number of PLS components is 10
 * then the number of 'ncomp' is 9 (2 components is minimum).
 *
 * @returns {number} the length of the results array.
 */
export function da_getreslength(nrows, nresp, ncomp) {
   // Structure of DA results:

   if (!Number.isSafeInteger(ncomp) || ncomp < 1) {
      throw new Error('da_getreslength: "ncomp" (number of DA blocks) must be a positive integer.');
   }

   if (nresp < 3 || nresp > 16) {
      throw new Error('da_getreslength: number of responses (classes) should be between 3 and 16.');
   }

   const svd_dim = nresp - 1;
   const nr_ncomp = nrows * ncomp;
   const nr_nc_ncomp = nresp * nr_ncomp;


   return 3 +                // nr, nc, ncomp
      svd_dim * nr_ncomp +   // U
      nr_nc_ncomp +          // Dh
      nr_nc_ncomp +          // Ds
      nr_nc_ncomp;           // Dr
}

/**
 * Returns length of array with DA model.
 *
 * @param {number} nresp number of responses (classes).
 * @param {number} ncomp number of DA blocks (pls_ncomp - nresp + 2).
 *
 * @returns {number} the length of the model array.
 */
export function da_getmodellength(nresp, ncomp) {

   if (!Number.isSafeInteger(ncomp) || ncomp < 1) {
      throw new Error('da_getmodellength: "ncomp" (number of DA blocks) must be a positive integer.');
   }

   if (nresp < 3 || nresp > 16) {
      throw new Error('da_getmodellength: number of responses (classes) should be between 3 and 16.');
   }

   const svd_dim = nresp - 1;
   const nc_ncomp = nresp * ncomp;
   const nc_svd_dim_ncomp = svd_dim * nc_ncomp;
   const svd_dim_ncomp = svd_dim * ncomp;

   return 3  +                            // nr, nc, ncomp
      nc_ncomp +                          // mean for each predicted Yp
      nc_ncomp +                          // sd for each predicted Yp
      (nc_svd_dim_ncomp) +                // loadings (V)
      (nc_svd_dim_ncomp) +                // loadings (VV = V * 𝚺^(-1))
      svd_dim_ncomp +                     // global center (u0)
      nc_svd_dim_ncomp +                  // centroids (Z)
      (nresp * svd_dim * svd_dim_ncomp) +    // inverse covariance matrix (Sinv)
      (nresp * svd_dim * svd_dim_ncomp) +    // inverse covariance matrix (Sinvr)
      3 * nc_ncomp +                      // 3 x d0 (h, s, r)
      3 * nc_ncomp;                       // 3 x Nd (h, s, r)
}


/**
 * Returns part of DA model array for given outcome.
 *
 * @param {Object} m JSON with DA model created by `da_fit()`.
 * @param {string} name name of the outcome (e.g. 'V', 'Z', 'hd0', etc.).
 * @param {number} a index of the DA model from 0 to m.ncomp - 1.
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function damodel_get(m, name, a) {

   if (!m || !m.class || !m.class.includes("damodel")) {
      throw new Error('damodel_get: parameter "m" must be JSON with DA model.');
   }

   const nresp = m.nresp;
   const ncomp = m.ncomp;
   const svd_dim = nresp - 1;
   const sk_size = svd_dim * svd_dim;

   // require a safe integer DA model index to prevent coercion and incorrect slicing
   if (!Number.isSafeInteger(a) || a < 0 || a >= ncomp) {
      throw new Error('damodel_get: component index "a" (' + a + ') must be an integer from 0 to ' +
         (ncomp - 1) + '.');
   }

   let length;
   let offset;

   const offset_m = 3;
   const length_ma = nresp;
   const length_m = length_ma * ncomp;

   const offset_s = offset_m + length_m;
   const length_sa = nresp;
   const length_s = length_sa * ncomp;

   const offset_V = offset_s + length_s;
   const length_Va = nresp * svd_dim;
   const length_V = length_Va * ncomp;

   const offset_VV = offset_V + length_V;
   const length_VVa = nresp * svd_dim;
   const length_VV = length_VVa * ncomp;

   const offset_u0 = offset_VV + length_VV;
   const length_u0a = svd_dim;
   const length_u0 = length_u0a * ncomp;

   const offset_Z = offset_u0 + length_u0;
   const length_Za = nresp * svd_dim;
   const length_Z = length_Za * ncomp;

   const offset_Sinv = offset_Z + length_Z;
   const length_Sinv_a = sk_size * nresp;
   const length_Sinv = length_Sinv_a * ncomp;

   const offset_Sinvr = offset_Sinv + length_Sinv;
   const length_Sinvr_a = sk_size * nresp;
   const length_Sinvr = length_Sinvr_a * ncomp;

   const offset_hd0 = offset_Sinvr + length_Sinvr;
   const length_hd0_a = nresp;
   const length_hd0 = length_hd0_a * ncomp;

   const offset_sd0 = offset_hd0 + length_hd0;
   const length_sd0_a = nresp;
   const length_sd0 = length_sd0_a * ncomp;

   const offset_rd0 = offset_sd0 + length_sd0;
   const length_rd0_a = nresp;
   const length_rd0 = length_rd0_a * ncomp;

   const offset_hNd = offset_rd0 + length_rd0;
   const length_hNd_a = nresp;
   const length_hNd = length_hNd_a * ncomp;

   const offset_sNd = offset_hNd + length_hNd;
   const length_sNd_a = nresp;
   const length_sNd = length_sNd_a * ncomp;

   const offset_rNd = offset_sNd + length_sNd;
   const length_rNd_a = nresp;
   const length_rNd = length_rNd_a * ncomp;

   switch (name) {
      case 'mY':
         offset = offset_m + a * length_ma;
         length = length_ma;
         break;
      case 'sY':
         offset = offset_s + a * length_sa;
         length = length_sa;
         break;
      case 'V':
         offset = offset_V + a * length_Va;
         length = length_Va;
         break;
      case 'VV':
         offset = offset_VV + a * length_VVa;
         length = length_VVa;
         break;
      case 'u0':
         offset = offset_u0 + a * length_u0a;
         length = length_u0a;
         break;
      case 'Z':
         offset = offset_Z + a * length_Za;
         length = length_Za;
         break;
      case 'Sinv':
      case 'sSinv':
         offset = offset_Sinv + a * length_Sinv_a;
         length = length_Sinv_a;
         break;
      case 'rSinv':
      case 'Sinvr':
         offset = offset_Sinvr + a * length_Sinvr_a;
         length = length_Sinvr_a;
         break;
      case 'hd0':
         offset = offset_hd0 + a * length_hd0_a;
         length = length_hd0_a;
         break;
      case 'sd0':
         offset = offset_sd0 + a * length_sd0_a;
         length = length_sd0_a;
         break;
      case 'rd0':
         offset = offset_rd0 + a * length_rd0_a;
         length = length_rd0_a;
         break;
      case 'hNd':
         offset = offset_hNd + a * length_hNd_a;
         length = length_hNd_a;
         break;
      case 'sNd':
         offset = offset_sNd + a * length_sNd_a;
         length = length_sNd_a;
         break;
      case 'rNd':
         offset = offset_rNd + a * length_rNd_a;
         length = length_rNd_a;
         break;

      default:
         throw Error('damodel_get: wrong outcome name.');
   }

   return m.v.subarray(offset, offset + length);
}


/**
 * Returns part of DA result array for given outcome.
 *
 * @param {Object} r JSON with DA results created by `da_predict()`.
 * @param {string} name name of the outcome (e.g. 'U').
 * @param {number} a index of the DA model from 0 to r.ncomp - 1.
 *
 * @returns {TypedArray} array with outcomes (by reference).
 */
export function dares_get(r, name, a) {

   if (!r || !r.class || !r.class.includes("dares")) {
      throw new Error('dares_get: parameter "r" must be JSON with DA results.');
   }

   const nrows = r.nrows;
   const nresp = r.nresp;
   const ncomp = r.ncomp;
   const svd_dim = nresp - 1;

   // require a safe integer DA model index to prevent coercion and incorrect slicing
   if (!Number.isSafeInteger(a) || a < 0 || a >= r.ncomp) {
      throw new Error('dares_get: component index "a" (' + a + ') must be an integer from 0 to ' +
         (ncomp - 1) + '.');
   }

   const offset_U = 3;
   const length_Ua = nrows * svd_dim;
   const length_U = length_Ua * ncomp;

   const offset_Dh = offset_U + length_U;
   const length_Dh_a = nrows * nresp;
   const length_Dh = length_Dh_a * ncomp;

   const offset_Ds = offset_Dh + length_Dh;
   const length_Ds_a = nrows * nresp;
   const length_Ds = length_Ds_a * ncomp;

   const offset_Dr = offset_Ds + length_Ds;
   const length_Dr_a = nrows * nresp;
   const length_Dr = length_Dr_a * ncomp;

   let offset, length;
   switch (name) {
      case 'U':
         offset = offset_U + a * length_Ua;
         length = length_Ua;
         break;
      case 'Dh':
         offset = offset_Dh + a * length_Dh_a;
         length = length_Dh_a;
         break;
      case 'Ds':
         offset = offset_Ds + a * length_Ds_a;
         length = length_Ds_a;
         break;
      case 'Dr':
         offset = offset_Dr + a * length_Dr_a;
         length = length_Dr_a;
         break;
      default:
         throw Error('dares_get: wrong outcome name.');
   }

   return r.v.subarray(offset, offset + length);
}


/**
 * Creates a DA model using wasm implementation.
 *
 * @param {Object} Module object with wasm module for PLS-DA.
 * @param {TypedArray} yp array with PLS predictions for training set (nrows x nresp x pls_ncomp).
 * @param {Array|Int32Array} classind array with class indices for training set, from 0 to nresp - 1;
 * every class needs at least nresp rows.
 * @param {number} nrows number of rows in the training set.
 * @param {number} nresp number of responses (classes), from 3 to 16.
 * @param {number} pls_ncomp number of components in related PLS model, at least nresp - 1.
 * @param {Array} classNames names of the classes, kept in the model.
 *
 * @returns {Object} JSON with DA model.
 */
export function da_fit(Module, yp, classind, nrows, nresp, pls_ncomp, classNames) {

   if (!Number.isSafeInteger(nresp) || nresp < 3 || nresp > 16) {
      throw new Error('da_fit: number of classes "nresp" (' + nresp +
         ') must be an integer from 3 to 16.');
   }

   if (!Number.isSafeInteger(nrows) || nrows < 1) {
      throw new Error('da_fit: number of rows "nrows" (' + nrows + ') must be a positive integer.');
   }

   if (!Number.isSafeInteger(pls_ncomp) || pls_ncomp < nresp - 1) {
      throw new Error('da_fit: number of PLS components "pls_ncomp" (' + pls_ncomp +
         ') must be an integer of at least nresp - 1 (' + (nresp - 1) + ').');
   }

   // a shorter or longer array would be copied from the wrong place without any error
   if (!ArrayBuffer.isView(yp) || yp.length !== nrows * nresp * pls_ncomp) {
      throw new Error('da_fit: "yp" must be a typed array with nrows x nresp x pls_ncomp values.');
   }

   if (!classind || classind.length !== nrows) {
      throw new Error('da_fit: "classind" must have one class index for each of the ' + nrows +
         ' rows.');
   }

   for (let i = 0; i < nrows; i++) {
      const k = classind[i];
      if (!Number.isInteger(k) || k < 0 || k >= nresp) {
         throw new Error('da_fit: class index (' + k + ') of row ' + (i + 1) +
            ' must be an integer from 0 to ' + (nresp - 1) + '.');
      }
   }

   // C refuses it as well; checked here so the message names the class
   const counts = new Array(nresp).fill(0);
   for (let i = 0; i < nrows; i++) counts[classind[i]]++;
   for (let k = 0; k < nresp; k++) {
      if (counts[k] < nresp) {
         throw new Error('da_fit: class ' + (k + 1) + ' has ' + counts[k] +
            ' rows, at least nresp (' + nresp + ') are needed.');
      }
   }

   const min_ncomp = nresp - 1;
   const max_ncomp = pls_ncomp;
   const ncomp = max_ncomp - min_ncomp + 1;

   const yp_begin = (min_ncomp - 1) * nresp * nrows;
   const yp_end = yp_begin + nrows * nresp * ncomp;

   // pointers and the result copies are hoisted so the 'finally' below can free every allocation no
   // matter where a throw happens, and so the copies outlive the block. a pointer which was never
   // assigned stays 0, and '_free(0)' is a no-op, so the frees below need no guards
   const m_length = da_getmodellength(nresp, ncomp);
   const m_constructor = Float64Array;
   let yp_ptr = 0, classind_ptr = 0, m_ptr = 0;
   let model;

   try {
      // allocate memory in WASM heap space and copy Yp values
      yp_ptr = allocCopy(Module, 'HEAPF64', yp.subarray(yp_begin, yp_end), 'yp');

      // allocate memory in WASM heap space and copy class indices
      classind_ptr = allocCopy(Module, 'HEAP32', new Int32Array(classind), 'classind');

      // allocate memory in WASM heap space for the model
      m_ptr = alloc(Module, m_length * m_constructor.BYTES_PER_ELEMENT, 'model');

      // call WASM function for DA
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall(
         'dafit_wasm',
         [ 'number'], // return type
         [ 'number', 'number', 'number', 'number', 'number', 'number'],
         [yp_ptr, classind_ptr, nrows, nresp, ncomp, m_ptr],
      );

      if (res !== 1) {
         throw wasmError(Module, 'da_fit', mark);
      }

      // copy part of HEAP memory with model outcomes. these have to stay 'slice' - each makes an
      // independent buffer, while a 'subarray' would be a view into memory the 'finally' frees
      model = Module.HEAPF64.slice(
         m_ptr / m_constructor.BYTES_PER_ELEMENT,
         m_ptr / m_constructor.BYTES_PER_ELEMENT + m_length,
      );

   } finally {

      // freeing after a wasm trap is expected to be safe: the instance survives an ordinary trap
      // and every pointer above is either 0 or valid. a trap caused by earlier heap corruption
      // could still leave the allocator damaged, and a throw from here would then mask the original
      // error - accepted rather than guarded, since this build exports no abort flag
      Module._free(yp_ptr);
      Module._free(m_ptr);
      Module._free(classind_ptr);
   }

   // compute number of elements in each class
   const class_nrows = classind.reduce((c, x) => {
      c[x] = (c[x] || 0) + 1;
      return c;
   }, []);

   // return DA model object
   return {
      ncomp, nrows, class_nrows, nresp, pls_ncomp, classNames,
      class: ['damodel'],
      v: model
   };
}


/**
 * Applies a DA model to PLS predictions for a new dataset.
 *
 * @param {Object} Module object with wasm module for PLS-DA.
 * @param {Object} m JSON with DA model created by `da_fit()`.
 * @param {TypedArray} yp array with PLS predictions for the dataset (nrows x nresp x pls_ncomp).
 * @param {number} nrows number of rows in the dataset.
 * @param {Array} [refClassInd] reference class of each row, from 0, kept in the results as given;
 * without it the results have no reference.
 *
 * @returns {Object} JSON with DA results.
 */
export function da_predict(Module, m, yp, nrows, refClassInd) {

   if (!m || !m.class || !m.class.includes('damodel')) {
      throw new Error('da_predict: parameter "m" must be JSON with DA model.');
   }

   // before the buffer check, whose length function would refuse the count under its own name;
   // a count that is not an integer is left to that check, which reports the mismatch
   if (Number.isSafeInteger(m.nresp) && (m.nresp < 3 || m.nresp > 16)) {
      throw new Error('da_predict: model has ' + m.nresp + ' classes, 3 to 16 are needed.');
   }

   // the header order is [nrows, nresp, ncomp], and the length does not depend on nrows
   checkModelBuffer(m, 'da_predict', [[0, 'nrows'], [1, 'nresp'], [2, 'ncomp']],
      (nr, nc, ncomp) => da_getmodellength(nc, ncomp));

   if (m.pls_ncomp !== m.ncomp + m.nresp - 2) {
      throw new Error('da_predict: model has ' + m.nresp + ' classes, ' + m.ncomp + ' DA blocks and ' +
         m.pls_ncomp + ' PLS components, which do not fit together - the model is corrupt.');
   }

   if (!Number.isSafeInteger(nrows) || nrows < 1) {
      throw new Error('da_predict: number of rows "nrows" (' + nrows +
         ') must be a positive integer.');
   }

   const yp_length = nrows * m.nresp * m.pls_ncomp;
   if (!ArrayBuffer.isView(yp) || yp.length !== yp_length) {
      throw new Error('da_predict: "yp" must be a typed array with nrows x nresp x pls_ncomp values.');
   }

   const nresp = m.nresp;
   const ncomp = m.ncomp;
   const pls_ncomp = m.pls_ncomp

   const min_ncomp = nresp - 1;
   const max_ncomp = pls_ncomp;

   const yp_begin = (min_ncomp - 1) * nresp * nrows;
   const yp_end = yp_begin + nrows * nresp * ncomp;

   const r_length = da_getreslength(nrows, m.nresp, m.ncomp);
   const r_constructor = Float64Array;
   let yp_ptr = 0, m_ptr = 0, r_ptr = 0;
   let res_values;

   try {
      // allocate memory in WASM heap space and copy Yp values
      yp_ptr = allocCopy(Module, 'HEAPF64', yp.subarray(yp_begin, yp_end), 'yp');

      // allocate memory in WASM heap space and copy model values
      m_ptr = allocCopy(Module, 'HEAPF64', m.v, 'model');

      // allocate memory in WASM heap space for result values
      r_ptr = alloc(Module, r_length * r_constructor.BYTES_PER_ELEMENT, 'results');

      // call WASM function for DA prediction
      const mark = Module.errlines?.length ?? 0;
      const res = Module.ccall(
         'dapredict_wasm',
         ['number'], // return type
         ['number', 'number', 'number', 'number'], // argument types
         [yp_ptr, nrows, m_ptr, r_ptr],
      ); // arguments

      if (res !== 1) {
         throw wasmError(Module, 'da_predict', mark);
      }

      // copy part of HEAP memory with result values - 'slice', not 'subarray', see 'da_fit'
      res_values = Module.HEAPF64.slice(
         r_ptr / r_constructor.BYTES_PER_ELEMENT,
         r_ptr / r_constructor.BYTES_PER_ELEMENT + r_length,
      );

   } finally {
      Module._free(yp_ptr);
      Module._free(m_ptr);
      Module._free(r_ptr);
   }

   // return DA results
   const resobj = {
      class: ['dares'],
      v: res_values,
      classNames: m.classNames,
      pls_ncomp: m.pls_ncomp,
      ncomp: m.ncomp,
      nrows: nrows,
      nresp: nresp,
      nclasses: m.classNames.length,
      refClassInd: refClassInd,
   };


   return resobj;
}

